import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Alert,
  Chip,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  CheckCircle,
  Cancel,
  CloudUpload,
  Delete as DeleteIcon,
  ContentCopy,
  Autorenew,
} from '@mui/icons-material';
import { municipiosService, saudeService, monitoramentoService } from '../services/api';

interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
  ativo: boolean;
  // Parametrizacao do CVAT (NT 30/2025). Ficam nulos ate serem preenchidos aqui.
  porte_populacional?: number | null;
  populacao_ibge?: number | null;
}

/**
 * Porte populacional do Anexo XCIX da Portaria de Consolidacao GM/MS 6/2017.
 * Define a "populacao parametro" de cada equipe, que e o DENOMINADOR das duas
 * dimensoes do CVAT. Nao existe no banco do PEC - e dado do municipio.
 */
const PORTES_POPULACIONAIS = [
  { valor: 1, label: '1 - Até 20 mil habitantes', parametros: 'eSF 2.000 | eAP 30h 1.500 | eAP 20h 1.000' },
  { valor: 2, label: '2 - Acima de 20 mil até 50 mil', parametros: 'eSF 2.500 | eAP 30h 1.875 | eAP 20h 1.250' },
  { valor: 3, label: '3 - Acima de 50 mil até 100 mil', parametros: 'eSF 2.750 | eAP 30h 2.063 | eAP 20h 1.375' },
  { valor: 4, label: '4 - Acima de 100 mil', parametros: 'eSF 3.000 | eAP 30h 2.250 | eAP 20h 1.500' },
];

interface Conexao {
  id: number;
  municipio_id: number;
  nome: string;
  host: string;
  porta: number;
  banco: string;
  usuario: string;
  esquema?: string;
  ssl: boolean;
  ativo: boolean;
  usa_dwh?: boolean;
  usa_snapshot?: boolean;
  modo_pec?: 'direto' | 'xml';
  ultimo_teste_ok: boolean | null;
  ultimo_teste_em: string | null;
}

/** Item do catalogo de parametros com o valor do municipio (GET /municipios/:id/parametros). */
interface Parametro {
  id: number;
  chave: string;
  rotulo: string;
  descricao: string | null;
  tipo: 'texto' | 'numero' | 'booleano' | 'imagem' | 'lista' | 'segredo' | 'codigo';
  grupo: string;
  opcoes: { valor: string; rotulo: string }[] | null;
  valor_padrao: string | null;
  obrigatorio: boolean;
  valor: string | null;
  /** Tipo 'segredo': ha valor gravado (o valor nunca vem da API). */
  definido?: boolean;
  atualizado_em?: string | null;
  /** Codigo da URL do PEC: endereco pronto para cadastrar no PEC. */
  url_integracao?: string | null;
}

/** Marcadores trocados com o backend (municipio-parametros.service.ts). */
const SEGREDO_REMOVER = '__remover__';
const CODIGO_GERAR = '__gerar__';

// Rotulos dos grupos de parametros (chave ASCII do banco -> rotulo acentuado).
const GRUPO_LABELS: Record<string, string> = {
  geral: 'Geral',
  relatorios_pdf: 'Relatórios em PDF',
  integracao_pec: 'Integração com o PEC',
};

const ESUS_FORM_INITIAL = {
  host: '',
  porta: 5432,
  banco: 'esus',
  usuario: '',
  senha: '',
  ssl: false,
  usa_dwh: false,
  usa_snapshot: false,
  modo_pec: 'direto' as 'direto' | 'xml',
};

const DW_FORM_INITIAL = {
  host: '',
  porta: 5432,
  banco: 'estatisticas_db',
  esquema: '',
  usuario: '',
  senha: '',
  ssl: false,
};

const IMAGEM_MAX_BYTES = 2 * 1024 * 1024;

// Tempo de exibicao da mensagem de sucesso (o usuario pode fechar antes pelo X).
const SUCESSO_DURACAO_MS = 8000;

// Abas: slug ASCII na URL (?aba=) -> rotulo. Conexoes e Parametros so existem na edicao.
const ABAS = [
  { slug: 'dados', label: 'Dados básicos' },
  { slug: 'conexoes', label: 'Conexões' },
  { slug: 'parametros', label: 'Parâmetros' },
];

const MunicipioFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const municipioId = id ? parseInt(id, 10) : null;
  const isNovo = municipioId === null;

  const abasVisiveis = isNovo ? ABAS.slice(0, 1) : ABAS;
  const abaAtual = Math.max(0, abasVisiveis.findIndex((a) => a.slug === searchParams.get('aba')));

  const [loading, setLoading] = useState(!isNovo);
  const [municipio, setMunicipio] = useState<Municipio | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    codigo_ibge: '',
    nome: '',
    porte_populacional: '' as string | number,
    populacao_ibge: '' as string | number,
  });
  const [esusForm, setEsusForm] = useState(ESUS_FORM_INITIAL);
  const [conexaoExistente, setConexaoExistente] = useState<Conexao | null>(null);
  const [dwForm, setDwForm] = useState(DW_FORM_INITIAL);
  const [conexaoDwExistente, setConexaoDwExistente] = useState<Conexao | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; latency?: number } | null>(null);
  const [testingDw, setTestingDw] = useState(false);
  const [testResultDw, setTestResultDw] = useState<{ ok: boolean; message: string; latency?: number } | null>(null);

  const [parametros, setParametros] = useState<Parametro[]>([]);
  const [valoresParam, setValoresParam] = useState<Record<string, string>>({});
  // Segredos em edicao (campo de senha aberto para substituir a chave atual).
  const [segredoEditando, setSegredoEditando] = useState<Record<string, boolean>>({});

  // Um timer por vez: sem isso, o timer de uma mensagem anterior apagaria a nova antes da hora.
  const sucessoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (sucessoTimer.current) clearTimeout(sucessoTimer.current); }, []);

  const mostrarSucesso = (msg: string) => {
    setSuccess(msg);
    if (sucessoTimer.current) clearTimeout(sucessoTimer.current);
    sucessoTimer.current = setTimeout(() => setSuccess(''), SUCESSO_DURACAO_MS);
  };

  // Mensagem vinda da criacao (/municipios/novo -> /municipios/:id). As duas rotas usam este
  // mesmo componente e o React NAO remonta ao navegar entre elas, por isso a mensagem e lida
  // a cada mudanca de location.state (e nao no estado inicial). Em seguida o state e limpo do
  // historico para a mensagem nao reaparecer ao recarregar a pagina.
  useEffect(() => {
    const msg = (location.state as any)?.success;
    if (msg) {
      mostrarSucesso(msg);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const aplicarParametros = (lista: Parametro[]) => {
    setParametros(lista);
    const valores: Record<string, string> = {};
    for (const p of lista) valores[p.chave] = p.valor ?? '';
    setValoresParam(valores);
    setSegredoEditando({});
  };

  const loadConexoes = useCallback(async () => {
    if (municipioId === null) return;
    try {
      const conexoes: Conexao[] = await saudeService.getConexoes(0);
      const conexao = conexoes.find((c) => c.municipio_id === municipioId) || null;
      setConexaoExistente(conexao);
      setEsusForm(
        conexao
          ? {
              host: conexao.host,
              porta: conexao.porta,
              banco: conexao.banco,
              usuario: conexao.usuario,
              senha: '',
              ssl: conexao.ssl,
              usa_dwh: conexao.usa_dwh || false,
              usa_snapshot: conexao.usa_snapshot || false,
              modo_pec: conexao.modo_pec || 'direto',
            }
          : ESUS_FORM_INITIAL,
      );
    } catch {
      // Silently fail - user may not have permission
    }
    try {
      const conexoesDw: Conexao[] = await monitoramentoService.getConexoesDw();
      const conexaoDw = conexoesDw.find((c) => c.municipio_id === municipioId) || null;
      setConexaoDwExistente(conexaoDw);
      setDwForm(
        conexaoDw
          ? {
              host: conexaoDw.host,
              porta: conexaoDw.porta,
              banco: conexaoDw.banco,
              esquema: conexaoDw.esquema || '',
              usuario: conexaoDw.usuario,
              senha: '',
              ssl: conexaoDw.ssl,
            }
          : DW_FORM_INITIAL,
      );
    } catch {
      // Silently fail
    }
  }, [municipioId]);

  const loadParametros = useCallback(async () => {
    if (municipioId === null) return;
    try {
      aplicarParametros(await municipiosService.getParametros(municipioId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar parâmetros do município');
    }
  }, [municipioId]);

  useEffect(() => {
    if (municipioId === null) return;
    (async () => {
      try {
        const data: Municipio | null = await municipiosService.getMunicipio(municipioId);
        if (!data) {
          setError('Município não encontrado');
          return;
        }
        setMunicipio(data);
        setFormData({
          codigo_ibge: data.codigo_ibge,
          nome: data.nome,
          porte_populacional: data.porte_populacional ?? '',
          populacao_ibge: data.populacao_ibge ?? '',
        });
      } catch {
        setError('Erro ao carregar município');
      } finally {
        setLoading(false);
      }
    })();
    loadConexoes();
    loadParametros();
  }, [municipioId, loadConexoes, loadParametros]);

  const handleTrocarAba = (_: React.SyntheticEvent, indice: number) => {
    setSearchParams({ aba: abasVisiveis[indice].slug }, { replace: true });
  };

  // ── Aba 1: dados basicos ──
  const handleSalvarDados = async () => {
    setError('');
    setSaving(true);
    try {
      if (isNovo) {
        const created = await municipiosService.createMunicipio(formData);
        // Segue direto para as conexoes, que so existem com o municipio criado.
        navigate(`/municipios/${created.id}?aba=conexoes`, {
          replace: true,
          state: { success: 'Município criado com sucesso! Configure agora as conexões.' },
        });
        return;
      }
      const updated = await municipiosService.updateMunicipio(municipioId as number, formData);
      setMunicipio(updated);
      mostrarSucesso('Dados básicos salvos com sucesso!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar município');
    } finally {
      setSaving(false);
    }
  };

  // ── Aba 2: conexoes ──
  const handleSalvarConexoes = async () => {
    if (municipioId === null) return;
    setError('');
    setSaving(true);
    const nome = municipio?.nome || formData.nome;
    try {
      // Save eSUS connection if host is filled
      if (esusForm.host && esusForm.usuario) {
        try {
          if (conexaoExistente) {
            const updateData: any = {
              nome: `e-SUS ${nome}`,
              host: esusForm.host,
              porta: esusForm.porta,
              banco: esusForm.banco,
              usuario: esusForm.usuario,
              ssl: esusForm.ssl,
              usa_dwh: esusForm.usa_dwh,
              usa_snapshot: esusForm.usa_snapshot,
              modo_pec: esusForm.modo_pec,
            };
            if (esusForm.senha) updateData.senha = esusForm.senha;
            await saudeService.updateConexao(conexaoExistente.id, updateData);
          } else {
            await saudeService.createConexao({
              municipio_id: municipioId,
              nome: `e-SUS ${nome}`,
              host: esusForm.host,
              porta: esusForm.porta,
              banco: esusForm.banco,
              usuario: esusForm.usuario,
              senha: esusForm.senha,
              ssl: esusForm.ssl,
              usa_dwh: esusForm.usa_dwh,
              usa_snapshot: esusForm.usa_snapshot,
              modo_pec: esusForm.modo_pec,
            });
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Erro ao salvar conexão e-SUS');
          return;
        }
      }

      // Save DW connection if host is filled
      if (dwForm.host && dwForm.usuario) {
        try {
          if (conexaoDwExistente) {
            const updateData: any = {
              nome: `DW ${nome}`,
              host: dwForm.host,
              porta: dwForm.porta,
              banco: dwForm.banco,
              esquema: dwForm.esquema,
              usuario: dwForm.usuario,
              ssl: dwForm.ssl,
            };
            if (dwForm.senha) updateData.senha = dwForm.senha;
            await monitoramentoService.updateConexaoDw(conexaoDwExistente.id, updateData);
          } else {
            await monitoramentoService.createConexaoDw({
              municipio_id: municipioId,
              nome: `DW ${nome}`,
              host: dwForm.host,
              porta: dwForm.porta,
              banco: dwForm.banco,
              esquema: dwForm.esquema,
              usuario: dwForm.usuario,
              senha: dwForm.senha,
              ssl: dwForm.ssl,
            });
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Erro ao salvar conexão DW');
          return;
        }
      }

      mostrarSucesso('Conexões salvas com sucesso!');
    } finally {
      await loadConexoes();
      setSaving(false);
    }
  };

  const handleTestConexao = async () => {
    if (!conexaoExistente) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await saudeService.testarConexao(conexaoExistente.id);
      setTestResult({
        ok: result.ok,
        message: result.ok ? 'Conexão bem-sucedida!' : result.error || 'Falha na conexão',
        latency: result.latency,
      });
      loadConexoes();
    } catch (err: any) {
      setTestResult({
        ok: false,
        message: err.response?.data?.message || 'Erro ao testar conexão',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleTestConexaoDw = async () => {
    if (!conexaoDwExistente) return;
    setTestingDw(true);
    setTestResultDw(null);
    try {
      const result = await monitoramentoService.testarConexaoDw(conexaoDwExistente.id);
      setTestResultDw({
        ok: result.ok,
        message: result.ok ? 'Conexão DW bem-sucedida!' : result.error || 'Falha na conexão DW',
        latency: result.latency,
      });
      loadConexoes();
    } catch (err: any) {
      setTestResultDw({
        ok: false,
        message: err.response?.data?.message || 'Erro ao testar conexão DW',
      });
    } finally {
      setTestingDw(false);
    }
  };

  // ── Aba 3: parametros ──
  const parametrosAlterados = useMemo(
    () => parametros.filter((p) => (valoresParam[p.chave] ?? '') !== (p.valor ?? '')),
    [parametros, valoresParam],
  );

  const parametrosPorGrupo = useMemo(() => {
    const grupos = new Map<string, Parametro[]>();
    for (const p of parametros) {
      if (!grupos.has(p.grupo)) grupos.set(p.grupo, []);
      grupos.get(p.grupo)!.push(p);
    }
    return Array.from(grupos.entries());
  }, [parametros]);

  const setValorParam = (chave: string, valor: string) => {
    setValoresParam((atual) => ({ ...atual, [chave]: valor }));
  };

  const handleImagemParam = (chave: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > IMAGEM_MAX_BYTES) {
      setError('Arquivo muito grande (máx. 2 MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setValorParam(chave, reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSalvarParametros = async () => {
    if (municipioId === null || parametrosAlterados.length === 0) return;
    setError('');
    setSaving(true);
    try {
      const itens = parametrosAlterados.map((p) => {
        const v = valoresParam[p.chave];
        return { chave: p.chave, valor: v === SEGREDO_REMOVER ? null : v || null };
      });
      aplicarParametros(await municipiosService.salvarParametros(municipioId, itens));
      mostrarSucesso('Parâmetros salvos com sucesso!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar parâmetros');
    } finally {
      setSaving(false);
    }
  };

  // Codigo gerado no servidor: salva so ele, na hora, sem descartar outras edicoes pendentes.
  const handleGerarCodigo = async (p: Parametro) => {
    if (municipioId === null) return;
    if (p.valor && !window.confirm(
      'Gerar um novo código muda o endereço da integração. O endereço cadastrado hoje no PEC deixa de funcionar '
      + 'até ser atualizado lá. Continuar?',
    )) return;
    setError('');
    setSaving(true);
    try {
      const lista: Parametro[] = await municipiosService.salvarParametros(municipioId, [{ chave: p.chave, valor: CODIGO_GERAR }]);
      setParametros(lista);
      setValoresParam((atual) => ({ ...atual, [p.chave]: lista.find((q) => q.chave === p.chave)?.valor ?? '' }));
      mostrarSucesso('Código gerado. Cadastre o novo endereço no PEC.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao gerar o código');
    } finally {
      setSaving(false);
    }
  };

  const copiarTexto = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      mostrarSucesso('Endereço copiado.');
    } catch {
      setError('Não foi possível copiar. Selecione o endereço e copie manualmente.');
    }
  };

  const textoPadrao = (p: Parametro): string => {
    if (p.valor_padrao === null || p.valor_padrao === '') return 'sem valor padrão';
    if (p.tipo === 'booleano') return p.valor_padrao === 'true' ? 'Sim' : 'Não';
    if (p.tipo === 'lista') return p.opcoes?.find((o) => o.valor === p.valor_padrao)?.rotulo || p.valor_padrao;
    if (p.tipo === 'imagem') return 'imagem padrão';
    return p.valor_padrao;
  };

  const renderParametro = (p: Parametro) => {
    const valor = valoresParam[p.chave] ?? '';
    const usandoPadrao = valor === '';
    const ajuda = [p.descricao, usandoPadrao ? `Padrão: ${textoPadrao(p)}` : null].filter(Boolean).join(' — ');
    const rotulo = p.obrigatorio ? `${p.rotulo} *` : p.rotulo;

    switch (p.tipo) {
      case 'segredo': {
        const removendo = valor === SEGREDO_REMOVER;
        const editando = segredoEditando[p.chave] || (!p.definido && !removendo);
        const quando = p.atualizado_em ? new Date(p.atualizado_em).toLocaleDateString('pt-BR') : null;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {rotulo}
            </Typography>
            {editando ? (
              <TextField
                fullWidth
                type="password"
                autoComplete="new-password"
                placeholder={p.definido ? 'Nova chave' : 'Cole aqui a chave gerada no PEC'}
                value={removendo ? '' : valor}
                onChange={(e) => setValorParam(p.chave, e.target.value)}
                helperText={p.descricao || undefined}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {removendo ? (
                  <Chip color="error" variant="outlined" label="Será removida ao salvar" />
                ) : (
                  <Chip color="success" variant="outlined" icon={<CheckCircle />} label={quando ? `Chave cadastrada em ${quando}` : 'Chave cadastrada'} />
                )}
                <Button size="small" onClick={() => { setValorParam(p.chave, ''); setSegredoEditando((a) => ({ ...a, [p.chave]: true })); }}>
                  Substituir
                </Button>
                {!removendo && (
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setValorParam(p.chave, SEGREDO_REMOVER)}>
                    Remover
                  </Button>
                )}
                {removendo && (
                  <Button size="small" onClick={() => setValorParam(p.chave, '')}>
                    Desfazer
                  </Button>
                )}
              </Box>
            )}
            {!editando && p.descricao && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {p.descricao}
              </Typography>
            )}
          </Box>
        );
      }
      case 'codigo':
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {rotulo}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                value={p.valor || ''}
                placeholder="Nenhum código gerado"
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 260, fontFamily: 'monospace' }}
              />
              <Button size="small" variant="outlined" startIcon={<Autorenew />} disabled={saving} onClick={() => handleGerarCodigo(p)}>
                {p.valor ? 'Gerar novo' : 'Gerar código'}
              </Button>
            </Box>
            {p.descricao && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {p.descricao}
              </Typography>
            )}
            {p.valor && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Endereço para cadastrar no PEC (Sistemas externos, local de acesso: Atendimento)
                </Typography>
                {p.url_integracao ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box
                      component="code"
                      sx={{ flex: 1, p: 1, bgcolor: '#FAFAFC', border: '1px solid #D3D4DD', borderRadius: '6px', fontSize: 12, wordBreak: 'break-all' }}
                    >
                      {p.url_integracao}
                    </Box>
                    <Button size="small" startIcon={<ContentCopy />} onClick={() => copiarTexto(p.url_integracao!)}>
                      Copiar
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    O servidor não tem o endereço público configurado (PEC_URL_PUBLICA). Procure o suporte.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );
      case 'numero':
        return (
          <TextField
            label={rotulo}
            fullWidth
            type="number"
            value={valor}
            onChange={(e) => setValorParam(p.chave, e.target.value)}
            helperText={ajuda || undefined}
          />
        );
      case 'booleano': {
        const efetivo = usandoPadrao ? p.valor_padrao === 'true' : valor === 'true';
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={efetivo}
                  onChange={(e) => setValorParam(p.chave, e.target.checked ? 'true' : 'false')}
                />
              }
              label={rotulo}
            />
            {ajuda && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
                {ajuda}
              </Typography>
            )}
          </Box>
        );
      }
      case 'lista':
        return (
          <TextField
            select
            label={rotulo}
            fullWidth
            value={valor}
            onChange={(e) => setValorParam(p.chave, e.target.value)}
            helperText={ajuda || undefined}
          >
            <MenuItem value="">
              <em>Usar padrão</em>
            </MenuItem>
            {(p.opcoes || []).map((o) => (
              <MenuItem key={o.valor} value={o.valor}>
                {o.rotulo}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'imagem': {
        const exibida = valor || p.valor_padrao || '';
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {rotulo}
            </Typography>
            <Box
              sx={{
                border: '1px dashed #D3D4DD',
                borderRadius: '8px',
                p: 2,
                mb: 1,
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#FAFAFC',
              }}
            >
              {exibida ? (
                <Box component="img" src={exibida} alt={p.rotulo} sx={{ maxHeight: 120, maxWidth: '100%' }} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem definida
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="outlined" size="small" component="label" startIcon={<CloudUpload />}>
                Enviar imagem
                <input hidden type="file" accept="image/*" onChange={handleImagemParam(p.chave)} />
              </Button>
              {valor && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setValorParam(p.chave, '')}
                >
                  Remover
                </Button>
              )}
            </Box>
            {ajuda && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {ajuda}
              </Typography>
            )}
          </Box>
        );
      }
      default:
        return (
          <TextField
            label={rotulo}
            fullWidth
            value={valor}
            onChange={(e) => setValorParam(p.chave, e.target.value)}
            helperText={ajuda || undefined}
          />
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const abaSlug = abasVisiveis[abaAtual].slug;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isNovo ? 'Novo município' : municipio?.nome || 'Município'}
          </Typography>
          {!isNovo && municipio && (
            <Typography variant="body2" color="text.secondary">
              Código IBGE {municipio.codigo_ibge}
              {!municipio.ativo && ' · Inativo'}
            </Typography>
          )}
        </Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/municipios')}>
          Voltar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Paper sx={{ borderRadius: '8px' }}>
        <Tabs
          value={abaAtual}
          onChange={handleTrocarAba}
          sx={{
            borderBottom: '1px solid #D3D4DD',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          {abasVisiveis.map((a) => (
            <Tab key={a.slug} label={a.label} />
          ))}
        </Tabs>

        {/* ===== Aba Dados básicos ===== */}
        {abaSlug === 'dados' && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código IBGE"
                  fullWidth
                  value={formData.codigo_ibge}
                  onChange={(e) => setFormData({ ...formData, codigo_ibge: e.target.value })}
                  inputProps={{ maxLength: 7 }}
                  helperText="7 dígitos do código IBGE do município"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nome"
                  fullWidth
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </Grid>

              {/*
                Porte populacional: define a populacao parametro de cada equipe, que e o
                DENOMINADOR das duas dimensoes do CVAT (NT 30/2025, Anexo XCIX). Nao existe
                no banco do PEC. Sem ele, o painel /saude/cvat fica bloqueado.
                Atencao: o porte afeta TODAS as equipes do municipio de uma vez.
              */}
              <Grid item xs={12} md={7}>
                <TextField
                  select
                  label="Porte Populacional"
                  fullWidth
                  value={formData.porte_populacional}
                  onChange={(e) => setFormData({ ...formData, porte_populacional: e.target.value })}
                  helperText={
                    formData.porte_populacional
                      ? PORTES_POPULACIONAIS.find((p) => p.valor === Number(formData.porte_populacional))?.parametros
                      : 'Necessário para o cálculo do CVAT (Vínculo e Acompanhamento Territorial)'
                  }
                >
                  <MenuItem value="">
                    <em>Não definido</em>
                  </MenuItem>
                  {PORTES_POPULACIONAIS.map((p) => (
                    <MenuItem key={p.valor} value={p.valor}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="População (IBGE)"
                  fullWidth
                  type="number"
                  value={formData.populacao_ibge}
                  onChange={(e) => setFormData({ ...formData, populacao_ibge: e.target.value })}
                  inputProps={{ min: 0 }}
                  helperText="Opcional — só usada em municípios muito pequenos"
                />
              </Grid>
            </Grid>

            {isNovo && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Depois de criar o município, as abas de conexões e parâmetros ficam disponíveis.
              </Typography>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSalvarDados}
                disabled={saving || !formData.codigo_ibge || !formData.nome}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isNovo ? 'Criar município' : 'Salvar dados básicos'}
              </Button>
            </Box>
          </Box>
        )}

        {/* ===== Aba Conexões ===== */}
        {abaSlug === 'conexoes' && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
              {/* ===== Conexão e-SUS PEC ===== */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Divider>
                    <Chip
                      label={esusForm.usa_dwh ? 'Conexão e-SUS (via Data Warehouse)' : 'Conexão e-SUS PEC'}
                      variant="outlined"
                      size="small"
                      color={esusForm.usa_dwh ? 'info' : 'default'}
                    />
                  </Divider>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
                    {esusForm.usa_dwh
                      ? 'Esta conexão aponta para um Data Warehouse. As queries de indicadores usarão o formato DWH.'
                      : 'Configure a conexão com o banco de dados e-SUS PEC deste município para consultar indicadores de saúde.'}
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={esusForm.usa_dwh}
                        onChange={(e) => setEsusForm({ ...esusForm, usa_dwh: e.target.checked })}
                        color="info"
                      />
                    }
                    label="Conectar via Data Warehouse"
                  />

                  {!esusForm.usa_dwh && (
                    <TextField
                      select
                      label="Origem dos cadastros"
                      fullWidth
                      value={esusForm.modo_pec}
                      onChange={(e) => setEsusForm({ ...esusForm, modo_pec: e.target.value as 'direto' | 'xml' })}
                      helperText={
                        esusForm.modo_pec === 'xml'
                          ? 'Os cadastros vêm de outro sistema por XML — as consultas usam a ficha de cadastro individual como fonte de nascimento, equipe, unidade e microárea.'
                          : 'Os cadastros são feitos dentro do PEC — as consultas usam as tabelas de cidadão do próprio PEC.'
                      }
                    >
                      <MenuItem value="direto">Cadastro feito no PEC</MenuItem>
                      <MenuItem value="xml">Cadastro importado por XML</MenuItem>
                    </TextField>
                  )}

                  {/* Vale para as duas origens de cadastro. O perfil XML ficou de fora até 13/09/2026
                      porque a busca dos dados do cidadão para montar a lista não era por chave ali; hoje
                      é, nos dois perfis. Segue indisponível para conexão via Data Warehouse. */}
                  {!esusForm.usa_dwh && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={esusForm.usa_snapshot}
                          onChange={(e) => setEsusForm({ ...esusForm, usa_snapshot: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Consolidação noturna (dados de ontem)"
                      sx={{ mt: -1 }}
                    />
                  )}
                  {!esusForm.usa_dwh && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: -2, ml: 6 }}>
                      Os indicadores passam a ser consolidados de madrugada e servidos sem consultar o
                      prontuário, com opção de ver em tempo real na própria tela. Recomendado em
                      municípios grandes, onde a consulta ao vivo concorre com os atendimentos.
                    </Typography>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        label="Host"
                        fullWidth
                        value={esusForm.host}
                        onChange={(e) => setEsusForm({ ...esusForm, host: e.target.value })}
                        placeholder="Ex: 192.168.1.100"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Porta"
                        fullWidth
                        type="number"
                        value={esusForm.porta}
                        onChange={(e) => setEsusForm({ ...esusForm, porta: parseInt(e.target.value, 10) || 5432 })}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    label="Banco de Dados"
                    fullWidth
                    value={esusForm.banco}
                    onChange={(e) => setEsusForm({ ...esusForm, banco: e.target.value })}
                    placeholder="Ex: esus"
                  />
                  <TextField
                    label="Usuário"
                    fullWidth
                    value={esusForm.usuario}
                    onChange={(e) => setEsusForm({ ...esusForm, usuario: e.target.value })}
                    placeholder="Ex: postgres"
                  />
                  <TextField
                    label={conexaoExistente ? 'Senha (deixe em branco para manter)' : 'Senha'}
                    fullWidth
                    type="password"
                    value={esusForm.senha}
                    onChange={(e) => setEsusForm({ ...esusForm, senha: e.target.value })}
                    helperText={conexaoExistente ? 'Deixe em branco para manter a senha atual' : undefined}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={esusForm.ssl}
                        onChange={(e) => setEsusForm({ ...esusForm, ssl: e.target.checked })}
                      />
                    }
                    label="Conexão SSL"
                  />

                  {conexaoExistente && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={testing ? <CircularProgress size={16} /> : <PlayArrow />}
                        onClick={handleTestConexao}
                        disabled={testing}
                      >
                        Testar Conexão
                      </Button>
                      {conexaoExistente.ultimo_teste_ok === true && (
                        <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />
                      )}
                      {conexaoExistente.ultimo_teste_ok === false && (
                        <Chip icon={<Cancel />} label="Falha" size="small" color="error" />
                      )}
                    </Box>
                  )}

                  {testResult && (
                    <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: -1 }}>
                      {testResult.message}
                      {testResult.latency != null && (
                        <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                          ({testResult.latency}ms)
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </Grid>

              {/* ===== Conexão Data Warehouse ===== */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Divider>
                    <Chip label="Conexão Data Warehouse (Monitoramento)" variant="outlined" size="small" />
                  </Divider>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
                    Configure a conexão com o banco de dados DW (estatísticas) deste município para os painéis de monitoramento.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        label="Host"
                        fullWidth
                        value={dwForm.host}
                        onChange={(e) => setDwForm({ ...dwForm, host: e.target.value })}
                        placeholder="Ex: 192.168.1.100"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Porta"
                        fullWidth
                        type="number"
                        value={dwForm.porta}
                        onChange={(e) => setDwForm({ ...dwForm, porta: parseInt(e.target.value, 10) || 5432 })}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    label="Banco de Dados"
                    fullWidth
                    value={dwForm.banco}
                    onChange={(e) => setDwForm({ ...dwForm, banco: e.target.value })}
                    placeholder="Ex: estatisticas_db"
                  />
                  <TextField
                    label="Esquema"
                    fullWidth
                    value={dwForm.esquema}
                    onChange={(e) => setDwForm({ ...dwForm, esquema: e.target.value })}
                    placeholder="Ex: sc_estatisticas"
                    helperText="Opcional. Vazio = schema padrão (public)."
                  />
                  <TextField
                    label="Usuário"
                    fullWidth
                    value={dwForm.usuario}
                    onChange={(e) => setDwForm({ ...dwForm, usuario: e.target.value })}
                    placeholder="Ex: postgres"
                  />
                  <TextField
                    label={conexaoDwExistente ? 'Senha (deixe em branco para manter)' : 'Senha'}
                    fullWidth
                    type="password"
                    value={dwForm.senha}
                    onChange={(e) => setDwForm({ ...dwForm, senha: e.target.value })}
                    helperText={conexaoDwExistente ? 'Deixe em branco para manter a senha atual' : undefined}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dwForm.ssl}
                        onChange={(e) => setDwForm({ ...dwForm, ssl: e.target.checked })}
                      />
                    }
                    label="Conexão SSL"
                  />

                  {conexaoDwExistente && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={testingDw ? <CircularProgress size={16} /> : <PlayArrow />}
                        onClick={handleTestConexaoDw}
                        disabled={testingDw}
                      >
                        Testar Conexão DW
                      </Button>
                      {conexaoDwExistente.ultimo_teste_ok === true && (
                        <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />
                      )}
                      {conexaoDwExistente.ultimo_teste_ok === false && (
                        <Chip icon={<Cancel />} label="Falha" size="small" color="error" />
                      )}
                    </Box>
                  )}

                  {testResultDw && (
                    <Alert severity={testResultDw.ok ? 'success' : 'error'} sx={{ mt: -1 }}>
                      {testResultDw.message}
                      {testResultDw.latency != null && (
                        <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                          ({testResultDw.latency}ms)
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSalvarConexoes}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Salvar conexões
              </Button>
            </Box>
          </Box>
        )}

        {/* ===== Aba Parâmetros ===== */}
        {abaSlug === 'parametros' && (
          <Box sx={{ p: 3 }}>
            {parametros.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum parâmetro cadastrado ainda.
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
                  Parâmetros específicos deste município. Campo em branco usa o valor padrão.
                </Typography>
                {parametrosPorGrupo.map(([grupo, itens]) => (
                  <Box key={grupo} sx={{ mb: 3 }}>
                    <Divider sx={{ mb: 2 }}>
                      <Chip label={GRUPO_LABELS[grupo] || grupo} variant="outlined" size="small" />
                    </Divider>
                    <Grid container spacing={2}>
                      {itens.map((p) => (
                        <Grid item xs={12} md={p.tipo === 'imagem' ? 12 : 6} key={p.chave}>
                          {renderParametro(p)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleSalvarParametros}
                    disabled={saving || parametrosAlterados.length === 0}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Salvar parâmetros
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MunicipioFormPage;
